import classNames from 'classnames'
import { FC, useCallback, useContext } from 'react'
import { AppraisalFlowContext } from '../../../modules/appraisal/appraisal-flow/shared/AppraisalFlowProvider'
import { generateStepName } from '../../../modules/appraisal/appraisal-flow/shared/util'
import { MeetingStep, MeetingStepStatus, MeetingType } from '../../../types'
import PerIcon from '../Icon'
import './index.less'

interface PerStepIndicatorProps {
  activeIndex: number
  animate: boolean
  processing: boolean
  meetingType: MeetingType
  steps: MeetingStep[]
  onClick: (step: MeetingStep, currentStepStatus?: MeetingStepStatus, callback?: () => void) => void
}

export const PerStepIndicator: FC<PerStepIndicatorProps> = ({ activeIndex, animate, processing, meetingType, steps, onClick }) => {
  const { currentStepState } = useContext(AppraisalFlowContext)
  const base = 'per-step-indicator'

  const generateStateModifier = useCallback(
    (step: MeetingStep, index: number) => {
      if (activeIndex === index) {
        return `${base}__item--active`
      } else if (step.status === MeetingStepStatus.Visited || step.status === MeetingStepStatus.Incomplete) {
        return `${base}__item--visited`
      } else if (step.status === MeetingStepStatus.Done) {
        return `${base}__item--done`
      } else {
        return `${base}__item--open`
      }
    },
    [activeIndex],
  )

  const noMargin = steps.length >= 9 // more than ten does not fit in box

  const renderIcon = (status: MeetingStepStatus, isActive: boolean, index: number) => {
    if (status === MeetingStepStatus.Done) {
      return <PerIcon name="check_stroke" color={isActive ? 'white' : 'primary'} size={14} />
    } else if (status === MeetingStepStatus.Incomplete) {
      return <PerIcon name="warning_fill" color={isActive ? 'white' : 'primary'} size={14} />
    } else {
      return <span>{index + 1}</span>
    }
  }

  return (
    <div className={base} data-e2e-appraisal-stepper>
      <div className={`${base}--wrapper`}>
        {steps.map((step, index) => {
          const isDone = step.status === MeetingStepStatus.Done
          const isIncomplete = step.status === MeetingStepStatus.Incomplete
          const isActive = index === activeIndex
          const isVisited = step.status === MeetingStepStatus.Visited
          const animatePreviousCurrentIndex = animate && index + 1 === activeIndex
          const clickable = !isActive && !processing
          const handleStepClick = () => {
            const activeViewId = steps[activeIndex].viewId
            if (clickable && currentStepState.viewId === activeViewId) {
              onClick(step, currentStepState.status)
            }
          }
          const stateModifier = generateStateModifier(step, index)

          return (
            <div className={`${base}__item-wrapper`} key={index}>
              <div
                className={classNames(`${base}__item`, stateModifier, {
                  [`${base}__item--pointer`]: clickable && currentStepState.viewId === steps[activeIndex].viewId,
                })}
                onClick={handleStepClick}
                data-e2e-stepper-viewid={step.viewId}
              >
                <div className={classNames(`${base}__circle`, { [`${base}__circle-animated`]: animate })}>
                  {renderIcon(step.status, isActive, index)}
                </div>
                <div
                  className={classNames(`${base}__description`, {
                    [`${base}__description--no-margin`]: noMargin,
                    [`${base}__description-animated`]: animate,
                  })}
                >
                  {generateStepName(step.viewId, meetingType)}
                </div>
              </div>

              {(isVisited || isDone || isIncomplete) && (
                <div className={`${base}-line-wrapper`}>
                  <div className={classNames(`${base}__line`, { [`${base}__line--animated`]: animatePreviousCurrentIndex })} />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
